import type { UseFinancingData } from '~/store-v2/financings/useFinancing'
import { match, P } from 'ts-pattern'
import { FEATURE_FLAGS } from '~/constants'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import { Formalization } from '~/types/enum'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'

interface FinancingItemForRoute {
  id?: string | number
  project_id?: string
  person_type?: string
  step?: string
  status?: string
  new_journey?: boolean | null
}

function getAnchorByStatus(item: unknown): string {
  const proposalStatuses = (item as UseFinancingData)?.section_statuses

  if (!proposalStatuses) {
    return ''
  }

  const finishedStatuses = P.union(GeneralStatuses.APPROVED, GeneralStatuses.REPROVED, GeneralStatuses.EXPIRED)

  return match(proposalStatuses)
    .with({ customer_data: { status: P.not(finishedStatuses) } }, () => Formalization.CUSTOMER_DATA)
    .with({ equipment: { status: GeneralStatuses.IN_PROGRESS } }, () => Formalization.EQUIPMENT)
    .with({ payment_model: { status: P.not(finishedStatuses) } }, () => Formalization.BIOMETRY_AND_CONTRACT)
    .with({ biometry_and_contract: { status: P.not(finishedStatuses) } }, () => Formalization.PAYMENT_MODEL)
    .otherwise(() => '')
}

export function useFinancingRoute() {
  const config = import.meta.env
  const {
    data: enableInstallationRedirectNewJourney,
  } = useFeatureFlag<boolean>(FEATURE_FLAGS.INSTALLATION_REDIRECT_NEW_JOURNEY, false, true)

  function buildRouteUrl(item: FinancingItemForRoute): string {
    const isNewJourneyEnabled = enableInstallationRedirectNewJourney.value
    const isOldJourney = item.new_journey === false

    if (isOldJourney) {
      if (isNewJourneyEnabled && item.step === 'installation' && item.status !== 'approved') {
        return `/installation/${item.project_id}`
      }
      return `${config.VITE_URL_LEGACY}/financiamento/etapas/${item.id}`
    }

    return match(item)
      .with({ step: 'simulation', status: 'expired' }, () => `/simulation/result/${item.project_id}`)
      .with({ step: P.union('customer_data', 'equipment', 'biometry_and_contract', 'payment_model'), status: 'expired' }, () => `/${item.person_type === 'PJ' ? 'company' : 'customer'}/${item.project_id}#${getAnchorByStatus(item)}`)
      .with({ step: 'installation', status: 'expired' }, () => `/installation/${item.project_id}`)
      .with({ step: P.union('simulation', 'expired') }, () => `/simulation/result/${item.project_id}`)
      .with({ step: P.union('customer_data', 'equipment', 'biometry_and_contract', 'payment_model') }, () => `/${item.person_type === 'PJ' ? 'company' : 'customer'}/${item.project_id}#${getAnchorByStatus(item)}`)
      .with({ step: P.union('installation', 'concluded') }, () => `/installation/${item.project_id}`)
      .otherwise(() => `/simulation/result/${item.project_id}`)
  }

  return {
    buildRouteUrl,
  }
}
